import { forwardRef, useState, ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { ArqueoTotals } from "@/lib/arqueo/aggregations";

interface Props {
  totals: ArqueoTotals;
  isPartial: boolean;
}

const fmtMoney = (n: number) =>
  `$${Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;

interface CollapsibleProps {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

const Collapsible = ({ title, count, open, onToggle, children }: CollapsibleProps) => (
  <Card className="p-2 bg-card/60 border-primary/10">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center justify-between gap-2 text-left hover:opacity-80 transition-opacity"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-primary/70">{title}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary/80 font-semibold">
          {count}
        </span>
      </div>
      <ChevronDown
        className={`h-3.5 w-3.5 text-primary/60 transition-transform duration-200 ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    {open && (
      <div className="mt-2 animate-in fade-in slide-in-from-top-1 duration-200">
        {count === 0 ? (
          <div className="text-xs text-primary/50">—</div>
        ) : (
          children
        )}
      </div>
    )}
  </Card>
);

export const ArqueoSummary = forwardRef<HTMLDivElement, Props>(({ totals, isPartial }, ref) => {
  const [methodOpen, setMethodOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);

  const methodCount = Object.keys(totals.byMethod).length;
  const categoryCount = Object.keys(totals.byCategory).length;
  const articlesCount = totals.articlesBreakdown.length;

  return (
    <div ref={ref} className="fixed bottom-0 left-0 right-0 z-30 border-t border-primary/20 bg-background/95 backdrop-blur-md shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.6)]">
      <div className="max-w-7xl mx-auto p-2 sm:p-4">
        <div className="flex flex-wrap items-start gap-2 sm:gap-4">
          <div className="flex-shrink-0">
            <div className="text-[10px] sm:text-xs text-primary/60 uppercase tracking-wide">
              {isPartial ? "Total filtrado" : "Total del día"}
            </div>
            <div className="text-xl sm:text-3xl font-bold text-primary leading-tight">
              {fmtMoney(totals.total)}
            </div>
            <div className="text-[10px] sm:text-xs text-primary/60">{totals.count} pagos</div>
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <Collapsible
              title="Por método"
              count={methodCount}
              open={methodOpen}
              onToggle={() => setMethodOpen((v) => !v)}
            >
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {Object.entries(totals.byMethod).map(([k, v]) => (
                  <span key={k}>
                    <span className="text-primary/60">{k}:</span>{" "}
                    <span className="font-semibold text-primary">{fmtMoney(v)}</span>
                  </span>
                ))}
              </div>
            </Collapsible>

            <Collapsible
              title="Por categoría"
              count={categoryCount}
              open={categoryOpen}
              onToggle={() => setCategoryOpen((v) => !v)}
            >
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {Object.entries(totals.byCategory).map(([k, v]) => (
                  <span key={k}>
                    <span className="text-primary/60">{k}:</span>{" "}
                    <span className="font-semibold text-primary">{fmtMoney(v)}</span>
                  </span>
                ))}
              </div>
            </Collapsible>

            <Collapsible
              title="Desglose Artículos"
              count={articlesCount}
              open={articlesOpen}
              onToggle={() => setArticlesOpen((v) => !v)}
            >
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {totals.articlesBreakdown.map((a) => (
                  <span key={a.article_id ?? a.name}>
                    <span className="text-primary/80">{a.name}</span>
                    <span className="text-primary/60"> x{a.quantity} = </span>
                    <span className="font-semibold text-primary">
                      {fmtMoney(a.subtotal)}
                    </span>
                  </span>
                ))}
              </div>
            </Collapsible>
          </div>
        </div>
      </div>
    </div>
  );
});
ArqueoSummary.displayName = "ArqueoSummary";
